// G5-1: read-only market reference for the re-sign / negotiation decision point.
// Derives a position -> salary distribution from the league's rostered contracts
// so each negotiation card can show "what the market pays a player at this
// position". Pure + unit-testable; builds no persistent structure (the worker
// recomputes it per view render from the already-cached player contracts).

export type PositionSalaryDatum = {
	pos: string;
	// Salary in the same unit the caller compares against (the negotiation view
	// passes per-year millions, matching getNegotiationAskingAmount).
	amount: number;
};

export type PositionSalaryStat = {
	median: number;
	p25: number;
	p75: number;
	count: number;
};

export type PositionSalaryMap = Record<string, PositionSalaryStat>;

// Below this many comparable contracts the median/quartiles are noise, so the
// caller suppresses the reference line rather than quoting a misleading figure.
export const MIN_MARKET_REF_SAMPLE = 3;

// Linear-interpolated quantile over an already-ascending array.
export const quantileSorted = (sorted: readonly number[], q: number): number => {
	if (sorted.length === 0) {
		return 0;
	}
	if (sorted.length === 1) {
		return sorted[0]!;
	}

	const idx = (sorted.length - 1) * q;
	const lo = Math.floor(idx);
	const hi = Math.ceil(idx);
	if (lo === hi) {
		return sorted[lo]!;
	}

	const frac = idx - lo;
	return sorted[lo]! * (1 - frac) + sorted[hi]! * frac;
};

export const derivePositionSalaryMap = (
	data: readonly PositionSalaryDatum[],
): PositionSalaryMap => {
	const byPos = new Map<string, number[]>();
	for (const datum of data) {
		if (typeof datum.pos !== "string" || datum.pos.length === 0) {
			continue;
		}
		if (!Number.isFinite(datum.amount) || datum.amount <= 0) {
			continue;
		}

		const amounts = byPos.get(datum.pos);
		if (amounts) {
			amounts.push(datum.amount);
		} else {
			byPos.set(datum.pos, [datum.amount]);
		}
	}

	const result: PositionSalaryMap = {};
	for (const [pos, amounts] of byPos) {
		amounts.sort((a, b) => a - b);
		result[pos] = {
			median: quantileSorted(amounts, 0.5),
			p25: quantileSorted(amounts, 0.25),
			p75: quantileSorted(amounts, 0.75),
			count: amounts.length,
		};
	}

	return result;
};

export type MarketComparisonKind = "above" | "below" | "near";

// Where the player's ask sits versus the position's market band. "near" means
// inside the typical p25..p75 range; outside it reads as above/below market.
export const getMarketComparison = (
	asking: number,
	stat: PositionSalaryStat,
): MarketComparisonKind => {
	if (asking > stat.p75) {
		return "above";
	}
	if (asking < stat.p25) {
		return "below";
	}
	return "near";
};

// Single gate the UI reuses so "enough comparable contracts" stays defined once.
export const hasMarketReference = (
	stat: PositionSalaryStat | undefined,
): stat is PositionSalaryStat =>
	stat !== undefined && stat.count >= MIN_MARKET_REF_SAMPLE;
